import type { CheckIssue, EnrichedResult, IssueGroup, Result } from "./types";

const ISSUE_COPY: Record<string, { message: string; action: string }> = {
  duplicate_override_key: {
    message: "Repeated payroll references make manual corrections unclear.",
    action:
      "Ask the agent to give each correction its own reference and check again.",
  },
  duplicate_accessor_key: {
    message: "Repeated payroll references make calculation results unclear.",
    action:
      "Ask the agent to give each result its own reference and check again.",
  },
  shared_final_pay_anchor: {
    message:
      "Employees share a total-pay reference. PayPacks can show the wrong total.",
    action: "Ask the agent to separate each employee's total and check again.",
  },
  candidate_execution_failed: {
    message: "The proposed calculation could not finish.",
    action: "Ask the agent to correct the calculation and run the check again.",
  },
  candidate_queue_timeout: {
    message: "The payroll check did not start within 10 minutes.",
    action: "Ask the agent to run the check again.",
  },
  candidate_execution_timeout: {
    message: "The payroll calculation exceeded its 20-minute limit.",
    action:
      "Ask the agent to review why the calculation took too long before running it again.",
  },
  requested_sheet_not_found: {
    message: "A requested payroll sheet is missing from the results.",
    action: "Ask the agent to restore the sheet and check again.",
  },
  statement_readiness_regression: {
    message: "A PayPack can no longer be prepared after this change.",
    action: "Ask the agent to correct the PayPack and check again.",
  },
  candidate_statement_not_ready: {
    message: "A proposed PayPack is not ready.",
    action: "Ask the agent to correct the PayPack and check again.",
  },
  requested_statement_target_not_found: {
    message: "A requested employee is missing from the results.",
    action: "Ask the agent to include the employee and check again.",
  },
  roster_override_did_not_change_output: {
    message: "The check did not confirm the intended roster change.",
    action:
      "Ask the agent to check the employee's roster values and calculation.",
  },
  dynamic_access_key: {
    message: "The change prevents a workbook access check.",
    action:
      "Ask the agent to correct the workbook access setup and check again.",
  },
  unbound_access_key: {
    message: "The check cannot confirm who can read a payroll sheet.",
    action: "Ask the agent to correct the sheet access setup and check again.",
  },
};
const UNKNOWN_COPY = {
  message: "A payroll check could not confirm that this change is ready.",
  action: "Ask the agent to correct the remaining check and run it again.",
};
const CHECK_TITLES = {
  failed: "Payroll checks need correction",
  passed: "Payroll checks passed",
  incomplete: "Checks incomplete",
} as const;

const groupIssues = (issues: CheckIssue[]): IssueGroup[] => {
  const groups = new Map<string, IssueGroup>();
  for (const issue of issues) {
    const code =
      issue.code && Object.hasOwn(ISSUE_COPY, issue.code)
        ? issue.code
        : "check_failed";
    const group = groups.get(code) ?? {
      code,
      ...(ISSUE_COPY[code] ?? UNKNOWN_COPY),
      count: 0,
      affected: [],
      affectedTruncated: false,
    };
    group.count += issue.count ?? 1;
    group.affected = Array.from(
      new Set([
        ...group.affected,
        ...(issue.sheet_names ?? []),
        ...(issue.technician_names ?? []),
      ]),
    );
    group.affectedTruncated ||= issue.affected_truncated === true;
    groups.set(code, group);
  }
  return Array.from(groups.values());
};

export const enrichResult = (result: Result): EnrichedResult => {
  const incomplete =
    result.incomplete === true ||
    result.acceptance?.issues_truncated === true ||
    result.statement_comparison?.targets_truncated === true;
  const checkStatus =
    result.error || result.ok === false || result.acceptance?.passed === false
      ? "failed"
      : result.ok === true && result.acceptance?.passed === true && !incomplete
        ? "passed"
        : "incomplete";
  return {
    ...result,
    checkStatus,
    checkTitle: CHECK_TITLES[checkStatus],
    issueGroups: groupIssues(result.acceptance?.issues ?? []),
    governanceMessages: (result.governance?.warnings ?? []).length
      ? [
          "Review payroll setup on the Roster and Access tabs before publishing.",
        ]
      : [],
    detailsTruncated:
      result.details_truncated === true ||
      !!result.truncation ||
      result.comparison?.sheets_truncated === true ||
      result.statement_comparison?.statement_details_truncated === true,
    incomplete,
  };
};
